import React from "react";
import { Icon, Button, Text } from "@blueprintjs/core";
import { inject, observer } from "mobx-react";
import { withRouter } from "react-router-dom";
import { NAVIGATION_ITEMS, NAVIGATION_ORDER } from "../../constants/navigation";
import LogoutButton from "../../../micro/utils/LogOutButton";
import "../../styles/layout.css";

class SideNavbar extends React.Component {
  state = {
    collapsed: false,
    expandedSections: ["MASTERS", "WORKFLOW", "REPORTS", "ADMINISTRATION"], // Expand all sections by default
  };

  componentDidMount = () => {
    // Add click listener to document for auto-close functionality
    document.addEventListener("click", this.handleDocumentClick);
  };

  componentWillUnmount = () => {
    // Remove click listener
    document.removeEventListener("click", this.handleDocumentClick);
  };

  handleDocumentClick = (event) => {
    // Check if click is outside the sidebar (desktop mode only)
    const sidebar = document.querySelector(".v3-sidebar");
    const menuButton = document.querySelector(".v3-mobile-menu-button");
    const isMobile = window.innerWidth <= 1024;

    // Ignore clicks on the menu button itself
    if (menuButton && (menuButton.contains(event.target) || menuButton === event.target)) {
      return;
    }

    if (isMobile && this.props.isMobileOpen && sidebar && !sidebar.contains(event.target)) {
      // On mobile, close the mobile sidebar
      this.props.onMobileClose?.();
    } else if (!isMobile && sidebar && !sidebar.contains(event.target) && !this.state.collapsed) {
      // On desktop, collapse the sidebar
      this.setState({ collapsed: true });
    }
  };

  toggleSidebar = () => {
    this.setState((prev) => ({
      collapsed: !prev.collapsed,
      // When expanding, ensure default sections are open
      expandedSections: prev.collapsed ? ["MASTERS", "WORKFLOW", "REPORTS", "ADMINISTRATION"] : prev.expandedSections,
    }));
  };

  toggleSection = (sectionId) => {
    this.setState((prev) => ({
      expandedSections: prev.expandedSections.includes(sectionId)
        ? prev.expandedSections.filter((id) => id !== sectionId)
        : [...prev.expandedSections, sectionId],
    }));
  };

  isActive = (path) => {
    return this.props.location.pathname === path;
  };

  navigateTo = (path) => {
    this.props.history.push(path);

    // Close mobile sidebar when navigating
    const isMobile = window.innerWidth <= 1024;
    if (isMobile && this.props.isMobileOpen) {
      this.props.onMobileClose?.();
    }
  };

  renderNavItem = (item, isSubItem = false) => {
    const isActive = this.isActive(item.path);
    const className = `v3-nav-item ${isActive ? "active" : ""}`;

    const navItem = (
      <div
        key={item.id}
        className={className}
        onClick={() => this.navigateTo(item.path)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            this.navigateTo(item.path);
          }
        }}
        role="button"
        tabIndex={0}
        aria-label={item.label}
        title={item.label}
      >
        <Icon icon={item.icon} className="v3-nav-item-icon" />
        {!this.state.collapsed && <span className="v3-label">{item.label}</span>}
      </div>
    );

    return navItem;
  };

  renderCollapsedSection = (sectionKey) => {
    const section = NAVIGATION_ITEMS[sectionKey];
    const hasItems = section.items && section.items.length > 0;

    return (
      <div key={section.id} className="v3-collapsed-section">
        {/* Section header icon */}
        <div
          className="v3-collapsed-section-header"
          onClick={() => hasItems && this.toggleSection(sectionKey)}
          onKeyDown={(e) => {
            if ((e.key === "Enter" || e.key === " ") && hasItems) {
              e.preventDefault();
              this.toggleSection(sectionKey);
            }
          }}
          role="button"
          tabIndex={0}
          aria-label={section.label}
        >
          <Icon icon={section.icon} className="v3-nav-item-icon" />
        </div>

        {/* Section items */}
        {hasItems && this.state.expandedSections.includes(sectionKey) && (
          <div className="v3-collapsed-section-items">
            {section.items.map((item) => this.renderNavItem(item, true))}
          </div>
        )}
      </div>
    );
  };

  renderNavSection = (sectionKey) => {
    const section = NAVIGATION_ITEMS[sectionKey];
    const isExpanded = this.state.expandedSections.includes(sectionKey);
    const hasItems = section.items && section.items.length > 0;

    if (sectionKey === "SURGERIES") {
      // Surgeries is a direct link, not a section with items
      return this.renderNavItem(section);
    }

    // When collapsed, show vertical icon layout
    if (this.state.collapsed) {
      return this.renderCollapsedSection(sectionKey);
    }

    // When expanded, show normal section with title and items
    const sectionTitle = (
      <div
        className="v3-nav-section-title"
        onClick={() => hasItems && this.toggleSection(sectionKey)}
        onKeyDown={(e) => {
          if ((e.key === "Enter" || e.key === " ") && hasItems) {
            e.preventDefault();
            this.toggleSection(sectionKey);
          }
        }}
        role="button"
        tabIndex={0}
        aria-label={section.label}
        title={section.label}
      >
        <Icon icon={section.icon} />
        <span className="v3-label">{section.label}</span>
        {hasItems && (
          <Icon icon={isExpanded ? "chevron-down" : "chevron-right"} size={12} style={{ marginLeft: "auto" }} />
        )}
      </div>
    );

    return (
      <div key={section.id} className="v3-nav-section">
        {sectionTitle}

        {hasItems && isExpanded && (
          <div className="v3-nav-subsection">{section.items.map((item) => this.renderNavItem(item, true))}</div>
        )}
      </div>
    );
  };

  render() {
    const { collapsed } = this.state;
    const { isMobileOpen } = this.props;
    const sidebarClass = `v3-sidebar ${collapsed ? "collapsed" : ""} ${isMobileOpen ? "open" : ""}`;

    return (
      <div className={sidebarClass}>
        <div className="v3-sidebar-header">
          <div className="v3-logo">
            <span className="v3-logo-icon">✃</span>
            {!collapsed && <span>NewChart</span>}
          </div>
          <Button
            minimal
            small
            icon={collapsed ? "menu-open" : "menu-closed"}
            onClick={this.toggleSidebar}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            className="v3-sidebar-toggle"
          />
        </div>

        <nav className="v3-navigation">{NAVIGATION_ORDER.map((sectionKey) => this.renderNavSection(sectionKey))}</nav>

        <div
          style={{
            padding: "var(--v3-spacing-md) var(--v3-spacing-lg)",
            borderTop: "1px solid var(--border-light)",
            marginTop: "auto",
            background: "var(--bg-surface)",
          }}
        >
          {!collapsed && (
            <Text
              style={{
                fontSize: "var(--v3-font-size-sm)",
                color: "var(--text-secondary)",
                margin: 0,
                textAlign: "center",
                fontFamily: "var(--v3-font-family-sans)",
              }}
            >
              Ridley NewChart
            </Text>
          )}
        </div>
      </div>
    );
  }
}

export default inject("authStore")(observer(withRouter(SideNavbar)));
